import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type CommunityHeaderWidgetProps,
  type CommunityHeaderThemingProps,
  HEADER_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionCommunityHeader for imperative control.
 */
export interface LegionCommunityHeaderHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildCommunityHeaderWidgetUrl(
  props: CommunityHeaderWidgetProps
): string {
  const {
    baseUrl,
    title = "Community",
    showSearch = true,
    showStats = true,
    showOnline = true,
    communityUrl,
    theme = "light",
  } = props;

  const url = new URL("/widget/community-header", baseUrl);
  url.searchParams.set("title", title);
  url.searchParams.set("showSearch", String(showSearch));
  url.searchParams.set("showStats", String(showStats));
  url.searchParams.set("showOnline", String(showOnline));
  url.searchParams.set("theme", theme);

  if (communityUrl) {
    url.searchParams.set("communityUrl", communityUrl);
  }

  // Append theming params
  const themingKeys = Object.keys(HEADER_THEMING_PROP_TO_PARAM) as Array<
    keyof CommunityHeaderThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(HEADER_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionCommunityHeader – a React component that embeds the Legion
 * Community Header widget via an iframe.
 */
export const LegionCommunityHeader = forwardRef<
  LegionCommunityHeaderHandle,
  CommunityHeaderWidgetProps
>(function LegionCommunityHeader(props, ref) {
  const {
    title = "Community",
    showSearch,
    showStats,
    showOnline,
    communityUrl,
    theme = "light",
    height = "auto",
    width = "100%",
    className,
    style,
    onLoad,
    onError,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildCommunityHeaderWidgetUrl(props),
    [
      baseUrl, title, showSearch, showStats, showOnline, communityUrl, theme,
      ...Object.keys(HEADER_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  const handleLoad = useCallback(() => {
    onLoad?.({ widget: "community-header", theme });
  }, [theme, onLoad]);

  const handleError = useCallback(() => {
    onError?.("Failed to load community header widget");
  }, [onError]);

  const resolvedHeight = height === "auto" ? "120px" : height;

  return (
    <div
      className={className}
      style={{ width, height: resolvedHeight, ...style }}
      data-testid="legion-community-header"
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="Community Header Widget"
        width="100%"
        height="100%"
        frameBorder="0"
        scrolling="no"
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        style={{ border: "none" }}
      />
    </div>
  );
});

